/**
 * /awaiting-approval — landing page for org creators whose org is awaiting
 * system-admin approval (REQUIRE_USER_APPROVAL org flow).
 * Reached via error-handler pivot (403 org_awaiting_approval) OR via
 * create-first-org CTA on the select-org page.
 *
 * Optional search param: orgName — the newly created org name passed by
 * create-first-org-cta.tsx so the page can display a contextual message
 * without an extra API call.
 */

import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useTranslations } from "use-intl";
import { ArrowLeftRight, Clock, LogOut } from "lucide-react";
import { z } from "zod";
import { LandingShell } from "@/features/system-admin/components/landing-shell";
import Button from "@/components/ui/button/Button";
import { useAuthActions, useOrgActions, useOrgStore } from "@/stores";

// Optional search params — org name carried by create-first-org CTA.
const awaitingApprovalSearch = z.object({
  orgName: z.string().optional(),
});

export const Route = createFileRoute("/awaiting-approval")({
  validateSearch: awaitingApprovalSearch,
  beforeLoad: ({ search }) => redirectApprovedOrgFromAwaiting(search),
  component: AwaitingApprovalPage,
});

export function redirectApprovedOrgFromAwaiting(search?: { orgName?: string }): void {
  const currentOrg = useOrgStore.getState().currentOrg;
  if (!currentOrg) return;
  if (!search?.orgName || currentOrg.name === search.orgName) {
    throw redirect({ to: "/" });
  }
}

function AwaitingApprovalPage() {
  const t = useTranslations("system-admin.common");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout } = useAuthActions();
  const { clearOrg } = useOrgActions();
  const { orgName } = Route.useSearch();

  const handleLogout = async () => {
    await logout();
    navigate({ to: "/signin" });
  };

  const handleChooseOrg = () => {
    clearOrg();
    queryClient.clear();
    window.location.href = "/select-org?force=1";
  };

  // Show org-specific message when org name is available (e.g. after CTA creation),
  // fall back to generic message when arriving via the 403 pivot.
  const description = orgName
    ? t("landing.awaiting.descriptionWithName", { orgName })
    : t("landing.awaiting.description");

  return (
    <LandingShell
      icon={
        <div className="flex size-16 items-center justify-center rounded-2xl bg-[var(--primary)]/10 text-[var(--primary)]">
          <Clock className="size-8" aria-hidden />
        </div>
      }
      title={t("landing.awaiting.title")}
      description={description}
    >
      <div className="space-y-3">
        <Button
          onClick={handleChooseOrg}
          fullWidth
          startIcon={<ArrowLeftRight className="size-4" aria-hidden />}
        >
          {t("landing.actions.chooseOrg")}
        </Button>
        <Button
          variant="outline"
          onClick={handleLogout}
          fullWidth
          startIcon={<LogOut className="size-4" aria-hidden />}
        >
          {t("landing.actions.logout")}
        </Button>
      </div>
    </LandingShell>
  );
}
